import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  signIn,
  signOut,
  currentSession,
  completeNewPassword as completeNewPasswordChallenge,
} from "./CognitoAuth.js";

// Exported so a session can be provided directly — used by render harnesses
// and tests, which have no Cognito to authenticate against. Application code
// should go through useAuth() below rather than consuming this directly.
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = loading, null = signed out
  const [error, setError] = useState(null);
  // Set while a user is mid first-sign-in "set a new password" challenge.
  // { cognitoUser, userAttributes } | null
  const [newPasswordChallenge, setNewPasswordChallenge] = useState(null);

  useEffect(() => {
    currentSession()
      .then(setSession)
      .catch(() => setSession(null));
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "NEW_PASSWORD_REQUIRED") {
        setNewPasswordChallenge({ cognitoUser: result.cognitoUser, userAttributes: result.userAttributes });
        return "NEW_PASSWORD_REQUIRED";
      }
      setSession(result.session);
      return true;
    } catch (err) {
      setError(err.message || "Sign-in failed");
      return false;
    }
  }, []);

  const completeNewPassword = useCallback(
    async (newPassword) => {
      if (!newPasswordChallenge) return false;
      setError(null);
      try {
        const s = await completeNewPasswordChallenge(
          newPasswordChallenge.cognitoUser,
          newPassword,
          newPasswordChallenge.userAttributes
        );
        setSession(s);
        setNewPasswordChallenge(null);
        return true;
      } catch (err) {
        setError(err.message || "Could not set new password");
        return false;
      }
    },
    [newPasswordChallenge]
  );

  const cancelNewPassword = useCallback(() => {
    setNewPasswordChallenge(null);
    setError(null);
  }, []);

  const logout = useCallback(() => {
    signOut();
    setSession(null);
  }, []);

  const idToken = session ? session.getIdToken().getJwtToken() : null;
  const claims = session ? session.getIdToken().decodePayload() : null;

  return (
    <AuthContext.Provider
      value={{
        session,
        idToken,
        claims,
        loading: session === undefined,
        error,
        login,
        logout,
        newPasswordChallenge,
        completeNewPassword,
        cancelNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
